import {areSplineInterpolationPointsCoincident} from './normalizeSplineInterpolationPoints';

/**
 * 可参与闭合样条周期插值的三维点。
 */
export interface ClosedSplineInterpolationPointLike {
    /** X 坐标。 */
    x: number;
    /** Y 坐标。 */
    y: number;
    /** Z 坐标。 */
    z: number;
}

/**
 * 闭合样条插值结果点。
 */
export interface ClosedSplineInterpolationPoint {
    /** X 坐标。 */
    x: number;
    /** Y 坐标。 */
    y: number;
    /** Z 坐标。 */
    z: number;
}

/** 周期三对角系统主对角线系数。 */
const PERIODIC_SPLINE_MAIN_DIAGONAL = 4;
/** 周期三对角系统相邻控制点系数。 */
const PERIODIC_SPLINE_NEIGHBOR_DIAGONAL = 1;
/** 周期三次 B 样条插值右侧缩放系数。 */
const PERIODIC_SPLINE_RIGHT_HAND_SCALE = 6;
/** 三次 B 样条基函数分母。 */
const CUBIC_B_SPLINE_BASIS_DENOMINATOR = 6;

/**
 * 克隆闭合样条插值点。
 * @param point 原始点。
 * @returns 独立点对象。
 */
function cloneClosedSplinePoint(point: ClosedSplineInterpolationPointLike): ClosedSplineInterpolationPoint {
    return {
        x: point.x,
        y: point.y,
        z: point.z,
    };
}

/**
 * 读取三维点指定坐标轴的值。
 * @param point 三维点。
 * @param axis 坐标轴索引，0 表示 X，1 表示 Y，2 表示 Z。
 * @returns 指定轴坐标。
 */
function readPointAxisValue(point: ClosedSplineInterpolationPointLike, axis: number): number {
    if (axis === 0) {
        return point.x;
    }
    if (axis === 1) {
        return point.y;
    }
    return point.z;
}

/**
 * 通过三个坐标轴值创建三维点。
 * @param x X 坐标。
 * @param y Y 坐标。
 * @param z Z 坐标。
 * @returns 三维点。
 */
function createPointFromAxisValues(x: number, y: number, z: number): ClosedSplineInterpolationPoint {
    return {x, y, z};
}

//------ 闭合样条周期插值 开始------
/**
 * 解析闭合样条唯一插值点。
 * 说明：
 * 1. 绘制闭合时输入尾部通常会重复首点；
 * 2. 周期插值系统只需要唯一数据点，重复闭合点会让线性系统退化为尖角约束；
 * 3. 返回值始终是独立点对象，避免采样过程修改外部输入。
 * @param points 原始插值点。
 * @returns 去除尾部重复闭合点后的唯一插值点。
 */
export function resolveUniqueClosedSplineInterpolationPoints(
    points: readonly ClosedSplineInterpolationPointLike[],
): ClosedSplineInterpolationPoint[] {
    const uniquePoints = points.map(cloneClosedSplinePoint);
    if (uniquePoints.length < 2) {
        return uniquePoints;
    }

    const firstPoint = uniquePoints[0]!;
    const lastPoint = uniquePoints[uniquePoints.length - 1]!;
    if (!areSplineInterpolationPointsCoincident(firstPoint, lastPoint)) {
        return uniquePoints;
    }

    return uniquePoints.slice(0, -1);
}

/**
 * 求解普通三对角线性系统。
 * 说明：这是周期三对角 Sherman-Morrison 分解的内部步骤。
 * @param lowerDiagonal 下对角线，长度为 n。
 * @param mainDiagonal 主对角线，长度为 n。
 * @param upperDiagonal 上对角线，长度为 n。
 * @param rightHandSide 右侧向量。
 * @returns 解向量。
 */
function solveTridiagonalSystem(
    lowerDiagonal: readonly number[],
    mainDiagonal: readonly number[],
    upperDiagonal: readonly number[],
    rightHandSide: readonly number[],
): number[] {
    const count = rightHandSide.length;
    const modifiedUpperDiagonal = new Array<number>(count).fill(0);
    const modifiedRightHandSide = new Array<number>(count).fill(0);
    const result = new Array<number>(count).fill(0);

    let denominator = mainDiagonal[0]!;
    if (Math.abs(denominator) <= Number.EPSILON) {
        throw new Error('闭合样条三对角系统主元为零，无法求解。');
    }

    modifiedUpperDiagonal[0] = upperDiagonal[0]! / denominator;
    modifiedRightHandSide[0] = rightHandSide[0]! / denominator;

    for (let index = 1; index < count; index += 1) {
        denominator = mainDiagonal[index]! - lowerDiagonal[index]! * modifiedUpperDiagonal[index - 1]!;
        if (Math.abs(denominator) <= Number.EPSILON) {
            throw new Error('闭合样条三对角系统出现零主元，无法求解。');
        }

        modifiedUpperDiagonal[index] = index < count - 1 ? upperDiagonal[index]! / denominator : 0;
        modifiedRightHandSide[index] = (
            rightHandSide[index]! - lowerDiagonal[index]! * modifiedRightHandSide[index - 1]!
        ) / denominator;
    }

    result[count - 1] = modifiedRightHandSide[count - 1]!;
    for (let index = count - 2; index >= 0; index -= 1) {
        result[index] = modifiedRightHandSide[index]! - modifiedUpperDiagonal[index]! * result[index + 1]!;
    }

    return result;
}

/**
 * 求解周期三对角线性系统。
 * 说明：
 * 1. 闭合三次 B 样条通过 `P(i-1)+4P(i)+P(i+1)=6Q(i)` 约束穿过每个样条点；
 * 2. 首尾控制点互为相邻项，因此系统是周期三对角；
 * 3. 使用 Sherman-Morrison 分解，避免高频预览中进行通用矩阵求逆。
 * @param rightHandSide 右侧向量。
 * @returns 控制点在单个坐标轴上的解。
 */
function solvePeriodicSplineAxisValues(rightHandSide: readonly number[]): number[] {
    const count = rightHandSide.length;
    if (count < 3) {
        throw new Error('闭合样条周期插值至少需要 3 个唯一点。');
    }

    const lowerDiagonal = new Array<number>(count).fill(PERIODIC_SPLINE_NEIGHBOR_DIAGONAL);
    const mainDiagonal = new Array<number>(count).fill(PERIODIC_SPLINE_MAIN_DIAGONAL);
    const upperDiagonal = new Array<number>(count).fill(PERIODIC_SPLINE_NEIGHBOR_DIAGONAL);
    const alpha = PERIODIC_SPLINE_NEIGHBOR_DIAGONAL;
    const beta = PERIODIC_SPLINE_NEIGHBOR_DIAGONAL;
    const gamma = -mainDiagonal[0]!;

    // Sherman-Morrison 要求先把周期角点折入普通三对角系统。
    mainDiagonal[0] = mainDiagonal[0]! - gamma;
    mainDiagonal[count - 1] = mainDiagonal[count - 1]! - (alpha * beta) / gamma;
    lowerDiagonal[0] = 0;
    upperDiagonal[count - 1] = 0;

    const rightSolution = solveTridiagonalSystem(
        lowerDiagonal,
        mainDiagonal,
        upperDiagonal,
        rightHandSide,
    );
    const correctionVector = new Array<number>(count).fill(0);
    correctionVector[0] = gamma;
    correctionVector[count - 1] = alpha;
    const correctionSolution = solveTridiagonalSystem(
        lowerDiagonal,
        mainDiagonal,
        upperDiagonal,
        correctionVector,
    );

    const factor = (
        rightSolution[0]! + (beta * rightSolution[count - 1]!) / gamma
    ) / (
        1 + correctionSolution[0]! + (beta * correctionSolution[count - 1]!) / gamma
    );

    return rightSolution.map((value, index) => value - factor * correctionSolution[index]!);
}

/**
 * 解析闭合三次 B 样条控制点。
 * @param interpolationPoints 唯一插值点。
 * @returns 周期 B 样条控制点。
 */
export function resolvePeriodicSplineControlPoints(
    interpolationPoints: readonly ClosedSplineInterpolationPointLike[],
): ClosedSplineInterpolationPoint[] {
    const axisSolutions = [0, 1, 2].map((axis) => solvePeriodicSplineAxisValues(
        interpolationPoints.map((point) => readPointAxisValue(point, axis) * PERIODIC_SPLINE_RIGHT_HAND_SCALE),
    ));

    return interpolationPoints.map((_point, index) => createPointFromAxisValues(
        axisSolutions[0]![index]!,
        axisSolutions[1]![index]!,
        axisSolutions[2]![index]!,
    ));
}

/**
 * 取周期数组中的控制点。
 * @param points 控制点数组。
 * @param index 原始索引。
 * @returns 周期索引对应的控制点。
 */
function readPeriodicPoint(
    points: readonly ClosedSplineInterpolationPoint[],
    index: number,
): ClosedSplineInterpolationPoint {
    const count = points.length;
    const normalizedIndex = ((index % count) + count) % count;
    return points[normalizedIndex]!;
}

/**
 * 采样单个周期三次 B 样条区段。
 * @param controlPoints 周期 B 样条控制点。
 * @param segmentIndex 区段索引。
 * @param parameter 区段内参数，范围为 0 到 1。
 * @returns 区段采样点。
 */
function evaluatePeriodicSplineSegmentPoint(
    controlPoints: readonly ClosedSplineInterpolationPoint[],
    segmentIndex: number,
    parameter: number,
): ClosedSplineInterpolationPoint {
    const previousPoint = readPeriodicPoint(controlPoints, segmentIndex - 1);
    const currentPoint = readPeriodicPoint(controlPoints, segmentIndex);
    const nextPoint = readPeriodicPoint(controlPoints, segmentIndex + 1);
    const nextNextPoint = readPeriodicPoint(controlPoints, segmentIndex + 2);
    const oneMinusParameter = 1 - parameter;
    const previousBasis = (oneMinusParameter * oneMinusParameter * oneMinusParameter) / CUBIC_B_SPLINE_BASIS_DENOMINATOR;
    const currentBasis = (
        3 * parameter * parameter * parameter
        - 6 * parameter * parameter
        + 4
    ) / CUBIC_B_SPLINE_BASIS_DENOMINATOR;
    const nextBasis = (
        -3 * parameter * parameter * parameter
        + 3 * parameter * parameter
        + 3 * parameter
        + 1
    ) / CUBIC_B_SPLINE_BASIS_DENOMINATOR;
    const nextNextBasis = (parameter * parameter * parameter) / CUBIC_B_SPLINE_BASIS_DENOMINATOR;

    return {
        x: previousPoint.x * previousBasis
            + currentPoint.x * currentBasis
            + nextPoint.x * nextBasis
            + nextNextPoint.x * nextNextBasis,
        y: previousPoint.y * previousBasis
            + currentPoint.y * currentBasis
            + nextPoint.y * nextBasis
            + nextNextPoint.y * nextNextBasis,
        z: previousPoint.z * previousBasis
            + currentPoint.z * currentBasis
            + nextPoint.z * nextBasis
            + nextNextPoint.z * nextNextBasis,
    };
}

/**
 * 分配每个周期样条区段的采样段数。
 * 说明：
 * 1. 周期三次 B 样条的每个区段起点对应一个用户插值点；
 * 2. 按区段分配采样可以保证最终折线点列包含所有插值点；
 * 3. 余数优先分配到前面的区段，保证总采样段数与调用方预算一致。
 * @param segmentCount 总采样段数。
 * @param interpolationPointCount 唯一插值点数量。
 * @returns 每个周期区段的采样段数。
 */
function allocatePeriodicSplineSpanSegmentCounts(
    segmentCount: number,
    interpolationPointCount: number,
): number[] {
    const baseSegmentCount = Math.floor(segmentCount / interpolationPointCount);
    const remainderSegmentCount = segmentCount % interpolationPointCount;

    return Array.from({length: interpolationPointCount}, (_unused, index) => (
        baseSegmentCount + (index < remainderSegmentCount ? 1 : 0)
    ));
}

/**
 * 采样闭合周期插值样条。
 * 说明：
 * 1. 该采样穿过所有唯一插值点，并在闭合处保持周期三次 B 样条连续性；
 * 2. 最后会显式追加首个采样点，保证渲染和轮廓解析拿到闭合点列；
 * 3. 开链样条不应调用该函数，继续使用 Puppy CAD 内核 的开放插值链路。
 * @param points 闭合样条插值点，允许尾部重复首点。
 * @param segments 总采样段数。
 * @returns 闭合曲线采样点。
 */
export function sampleClosedSplineInterpolationPoints(
    points: readonly ClosedSplineInterpolationPointLike[],
    segments: number,
): ClosedSplineInterpolationPoint[] {
    const interpolationPoints = resolveUniqueClosedSplineInterpolationPoints(points);
    if (interpolationPoints.length < 3) {
        return interpolationPoints.map(cloneClosedSplinePoint);
    }

    const controlPoints = resolvePeriodicSplineControlPoints(interpolationPoints);
    const segmentCount = Math.max(interpolationPoints.length, Math.floor(segments));
    const spanSegmentCounts = allocatePeriodicSplineSpanSegmentCounts(segmentCount, interpolationPoints.length);
    const sampledPoints: ClosedSplineInterpolationPoint[] = [];

    for (let spanIndex = 0; spanIndex < spanSegmentCounts.length; spanIndex += 1) {
        const currentSpanSegmentCount = spanSegmentCounts[spanIndex]!;

        // 每个区段从参数 0 开始采样，因此用户插值点会稳定出现在采样点列里。
        for (let segmentIndex = 0; segmentIndex < currentSpanSegmentCount; segmentIndex += 1) {
            sampledPoints.push(evaluatePeriodicSplineSegmentPoint(
                controlPoints,
                spanIndex,
                segmentIndex / currentSpanSegmentCount,
            ));
        }
    }

    sampledPoints.push(cloneClosedSplinePoint(sampledPoints[0]!));
    return sampledPoints;
}
//------ 闭合样条周期插值 结束------
